import React from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminMachineSupport.css";

const createLinkDraft = () => ({
  localId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: "",
  label: "",
  url: "",
});

const createEmptyForm = () => ({
  title: "",
  description: "",
  licensedEnabled: true,
  realgemsEnabled: true,
  imageUrl: "",
  imageOriginalName: "",
  links: [createLinkDraft()],
});

const formatDateTime = (value) => {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_err) {
    return String(value);
  }
};

const formatFileSize = (size) => {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return "0 KB";
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
};

const moveItemById = (items, draggedId, targetId) => {
  if (!draggedId || !targetId || draggedId === targetId) {
    return items;
  }

  const sourceIndex = items.findIndex((item) => item.id === draggedId);
  const targetIndex = items.findIndex((item) => item.id === targetId);

  if (sourceIndex === -1 || targetIndex === -1) {
    return items;
  }

  const nextItems = [...items];
  const [draggedItem] = nextItems.splice(sourceIndex, 1);
  nextItems.splice(targetIndex, 0, draggedItem);

  return nextItems.map((item, index) => ({
    ...item,
    position: index + 1,
  }));
};

const toFormLinks = (links = []) =>
  Array.isArray(links) && links.length
    ? links.map((link) => ({
        localId: link.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        id: link.id || "",
        label: link.label || "",
        url: link.url || "",
      }))
    : [createLinkDraft()];

const AdminMachineSupport = () => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [machines, setMachines] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [reordering, setReordering] = React.useState(false);
  const [draggedMachineId, setDraggedMachineId] = React.useState("");
  const [deletingMachineId, setDeletingMachineId] = React.useState("");
  const [editingMachineId, setEditingMachineId] = React.useState("");
  const [updatedAt, setUpdatedAt] = React.useState("");
  const [form, setForm] = React.useState(createEmptyForm);
  const [selectedImageFile, setSelectedImageFile] = React.useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = React.useState("");
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const imageInputRef = React.useRef(null);

  const toggleMenu = () => setMenuClose((prev) => !prev);

  const resetForm = React.useCallback(() => {
    setForm(createEmptyForm());
    setSelectedImageFile(null);
    setSelectedImagePreview("");
    setEditingMachineId("");
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  }, []);

  const loadMachines = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await Api.get("/admin/machine-videos");
      setMachines(Array.isArray(response?.data?.items) ? response.data.items : []);
      setUpdatedAt(response?.data?.updatedAt || "");
    } catch (err) {
      console.error("Erro ao carregar maquinas:", err);
      setError("Não foi possível carregar as máquinas cadastradas.");
      setMachines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  React.useEffect(() => {
    if (!selectedImageFile) {
      setSelectedImagePreview("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(selectedImageFile);
    setSelectedImagePreview(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [selectedImageFile]);

  const handleFormChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;

    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setError("");
    setSuccess("");
  };

  const handleSelectImage = (event) => {
    const [file] = Array.from(event.target.files || []);
    setSelectedImageFile(file || null);
    setError("");
    setSuccess("");
  };

  const handleLinkChange = (localId, field, value) => {
    setForm((current) => ({
      ...current,
      links: current.links.map((link) =>
        link.localId === localId ? { ...link, [field]: value } : link
      ),
    }));
    setError("");
    setSuccess("");
  };

  const handleAddLink = () => {
    setForm((current) => ({
      ...current,
      links: [...current.links, createLinkDraft()],
    }));
  };

  const handleRemoveLink = (localId) => {
    setForm((current) => {
      const nextLinks = current.links.filter((link) => link.localId !== localId);
      return {
        ...current,
        links: nextLinks.length ? nextLinks : [createLinkDraft()],
      };
    });
  };

  const handleStartEdit = (machine) => {
    setEditingMachineId(machine.id);
    setForm({
      title: machine.title || "",
      description: machine.description || "",
      licensedEnabled: Boolean(machine.licensedEnabled),
      realgemsEnabled: Boolean(machine.realgemsEnabled),
      imageUrl: machine.imageUrl || "",
      imageOriginalName: machine.imageOriginalName || "",
      links: toFormLinks(machine.links),
    });
    setSelectedImageFile(null);
    setSelectedImagePreview("");
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
    setError("");
    setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildPayloadLinks = () =>
    form.links.map((link) => ({
      id: link.id || undefined,
      label: String(link.label || "").trim(),
      url: String(link.url || "").trim(),
    }));

  const handleSubmit = async (event) => {
    event.preventDefault();

    const title = String(form.title || "").trim();
    const description = String(form.description || "").trim();
    const links = buildPayloadLinks();

    if (!title) {
      setError("Informe o nome da máquina.");
      setSuccess("");
      return;
    }

    if (!description) {
      setError("Informe a descrição do produto.");
      setSuccess("");
      return;
    }

    if (!selectedImageFile && !form.imageUrl) {
      setError("Envie a foto da máquina.");
      setSuccess("");
      return;
    }

    if (!form.licensedEnabled && !form.realgemsEnabled) {
      setError("Selecione ao menos um portal para habilitar a máquina.");
      setSuccess("");
      return;
    }

    if (!links.some((link) => link.label && link.url)) {
      setError("Cadastre ao menos um link de apoio.");
      setSuccess("");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("licensedEnabled", form.licensedEnabled ? "true" : "false");
    formData.append("realgemsEnabled", form.realgemsEnabled ? "true" : "false");
    formData.append("links", JSON.stringify(links));

    if (selectedImageFile) {
      formData.append("image", selectedImageFile);
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      if (editingMachineId) {
        const response = await Api.put(`/admin/machine-videos/${editingMachineId}`, formData, {
          timeout: 120000,
        });
        const updatedItem = response?.data?.item;
        setMachines((current) =>
          current.map((item) => (item.id === editingMachineId ? updatedItem : item))
        );
        setSuccess("Máquina atualizada com sucesso.");
      } else {
        const response = await Api.post("/admin/machine-videos", formData, {
          timeout: 120000,
        });
        setMachines(Array.isArray(response?.data?.items) ? response.data.items : []);
        setUpdatedAt(response?.data?.updatedAt || "");
        setSuccess("Máquina cadastrada com sucesso.");
      }

      resetForm();
      await loadMachines();
    } catch (err) {
      console.error("Erro ao salvar máquina:", err);
      setError(err?.response?.data?.message || "Não foi possível salvar a máquina.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMachine = async (machineId) => {
    if (!window.confirm("Deseja remover esta máquina dos materiais de apoio?")) return;

    try {
      setDeletingMachineId(machineId);
      setError("");
      setSuccess("");

      await Api.delete(`/admin/machine-videos/${machineId}`);
      setMachines((current) =>
        current
          .filter((item) => item.id !== machineId)
          .map((item, index) => ({
            ...item,
            position: index + 1,
          }))
      );

      if (editingMachineId === machineId) {
        resetForm();
      }

      await loadMachines();
      setSuccess("Máquina removida com sucesso.");
    } catch (err) {
      console.error("Erro ao remover máquina:", err);
      setError(err?.response?.data?.message || "Não foi possível remover a máquina.");
    } finally {
      setDeletingMachineId("");
    }
  };

  const handleDragStart = (machineId) => {
    setDraggedMachineId(machineId);
    setError("");
    setSuccess("");
  };

  const handleDrop = async (targetId) => {
    if (!draggedMachineId) return;

    const previousItems = machines;
    const nextItems = moveItemById(previousItems, draggedMachineId, targetId);
    setDraggedMachineId("");

    if (nextItems === previousItems) {
      return;
    }

    try {
      setReordering(true);
      setMachines(nextItems);
      const response = await Api.put("/admin/machine-videos/reorder", {
        orderedIds: nextItems.map((item) => item.id),
      });
      setMachines(Array.isArray(response?.data?.items) ? response.data.items : nextItems);
      setUpdatedAt(response?.data?.updatedAt || "");
      setSuccess("Ordem das máquinas atualizada com sucesso.");
    } catch (err) {
      console.error("Erro ao reordenar máquinas:", err);
      setMachines(previousItems);
      setError(
        err?.response?.data?.message || "Não foi possível atualizar a ordem das máquinas."
      );
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} settingsActive="active" />

      <main className="main-content">
        <Topbar page="Configurações / Máquinas" onToggleMenu={toggleMenu} />

        <section className="machine-support-grid">
          <article className="machine-support-panel">
            <div className="machine-support-header">
              <div>
                <h1>Materiais de apoio das máquinas</h1>
                <p>
                  Cadastre cada máquina com foto, descrição e os links úteis para vídeo, manual
                  e demais materiais de apoio.
                </p>
              </div>
              <span className="machine-support-badge">Super administrador</span>
            </div>

            <div className="machine-support-split">
              <form className="machine-support-card" onSubmit={handleSubmit}>
                <div className="machine-support-card-header">
                  <h2>{editingMachineId ? "Editar máquina" : "Nova máquina"}</h2>
                  <p>
                    {editingMachineId
                      ? "Atualize os dados principais e mantenha os links sempre revisados."
                      : "Preencha o cadastro com o conteúdo que ficará visível no portal."}
                  </p>
                </div>

                <label className="machine-support-field" htmlFor="machine-title">
                  <span>Nome da máquina</span>
                  <input
                    id="machine-title"
                    type="text"
                    value={form.title}
                    onChange={handleFormChange("title")}
                    placeholder="Ex: Smart Air Pro 300"
                  />
                </label>

                <label className="machine-support-field" htmlFor="machine-description">
                  <span>Descrição do produto</span>
                  <textarea
                    id="machine-description"
                    rows={5}
                    value={form.description}
                    onChange={handleFormChange("description")}
                    placeholder="Descreva rapidamente a máquina, diferenciais e aplicações."
                  />
                </label>

                <div className="machine-support-field">
                  <span>Onde habilitar esta máquina</span>
                  <div className="machine-support-toggle-grid">
                    <label className="machine-support-toggle">
                      <input
                        type="checkbox"
                        checked={form.licensedEnabled}
                        onChange={handleFormChange("licensedEnabled")}
                      />
                      <span className="machine-support-toggle-switch" aria-hidden="true" />
                      <span>Licenciado</span>
                    </label>

                    <label className="machine-support-toggle">
                      <input
                        type="checkbox"
                        checked={form.realgemsEnabled}
                        onChange={handleFormChange("realgemsEnabled")}
                      />
                      <span className="machine-support-toggle-switch" aria-hidden="true" />
                      <span>Realgems</span>
                    </label>
                  </div>
                </div>

                <div className="machine-support-field">
                  <span>Foto da máquina</span>
                  <label className="machine-support-upload" htmlFor="machine-image">
                    <strong>{selectedImageFile ? "Trocar imagem" : "Selecionar imagem"}</strong>
                    <small>JPG, PNG, WEBP e similares. Limite de 10 MB.</small>
                  </label>
                  <input
                    ref={imageInputRef}
                    id="machine-image"
                    type="file"
                    accept="image/*"
                    onChange={handleSelectImage}
                  />

                  {selectedImageFile ? (
                    <div className="machine-support-file-pill">
                      <strong>{selectedImageFile.name}</strong>
                      <span>{formatFileSize(selectedImageFile.size)}</span>
                    </div>
                  ) : null}

                  {selectedImagePreview || form.imageUrl ? (
                    <div className="machine-support-image-preview">
                      <img
                        src={selectedImagePreview || form.imageUrl}
                        alt={form.imageOriginalName || form.title || "Preview da maquina"}
                      />
                    </div>
                  ) : (
                    <div className="machine-support-image-placeholder">
                      A foto enviada aparecerá aqui.
                    </div>
                  )}
                </div>

                <div className="machine-support-field">
                  <div className="machine-support-links-header">
                    <span>Links de apoio</span>
                    <button type="button" onClick={handleAddLink}>
                      Adicionar link
                    </button>
                  </div>

                  <div className="machine-support-links-list">
                    {form.links.map((link, index) => (
                      <div className="machine-support-link-row" key={link.localId}>
                        <label className="machine-support-field" htmlFor={`machine-link-label-${index}`}>
                          <span>Título do link</span>
                          <input
                            id={`machine-link-label-${index}`}
                            type="text"
                            value={link.label}
                            onChange={(event) =>
                              handleLinkChange(link.localId, "label", event.target.value)
                            }
                            placeholder="Ex: Vídeo no YouTube"
                          />
                        </label>

                        <label className="machine-support-field" htmlFor={`machine-link-url-${index}`}>
                          <span>URL</span>
                          <input
                            id={`machine-link-url-${index}`}
                            type="url"
                            value={link.url}
                            onChange={(event) =>
                              handleLinkChange(link.localId, "url", event.target.value)
                            }
                            placeholder="https://..."
                          />
                        </label>

                        <button
                          type="button"
                          className="machine-support-remove-link"
                          onClick={() => handleRemoveLink(link.localId)}
                          disabled={form.links.length === 1}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="machine-support-actions">
                  <button type="submit" disabled={submitting}>
                    {submitting
                      ? editingMachineId
                        ? "Salvando..."
                        : "Cadastrando..."
                      : editingMachineId
                        ? "Salvar máquina"
                        : "Cadastrar máquina"}
                  </button>

                  {editingMachineId ? (
                    <button type="button" className="is-secondary" onClick={resetForm}>
                      Cancelar edição
                    </button>
                  ) : null}
                </div>
              </form>

              <section className="machine-support-card">
                <div className="machine-support-card-header">
                  <h2>Resumo rápido</h2>
                  <p>Organize a biblioteca arrastando as linhas da tabela abaixo.</p>
                </div>

                <div className="machine-support-info-grid">
                  <div className="machine-support-info-item">
                    <strong>{machines.length}</strong>
                    <span>máquina(s) cadastrada(s)</span>
                  </div>
                  <div className="machine-support-info-item">
                    <strong>{reordering ? "Salvando..." : "Drag and drop"}</strong>
                    <span>ordenação rápida da biblioteca</span>
                  </div>
                </div>

                {updatedAt ? (
                  <p className="machine-support-help">
                    Última atualização: {formatDateTime(updatedAt)}
                  </p>
                ) : null}

                <div className="machine-support-tip-box">
                  <strong>Boas práticas</strong>
                  <span>Use uma foto nítida, uma descrição curta e links com títulos objetivos.</span>
                  <span>Exemplos: “Vídeo de instalação”, “Manual PDF”, “Catálogo comercial”.</span>
                </div>
              </section>
            </div>

            {error ? <div className="machine-support-message error">{error}</div> : null}
            {success ? <div className="machine-support-message success">{success}</div> : null}
          </article>

          <article className="machine-support-panel">
            <div className="machine-support-header compact">
              <div>
                <h2>Biblioteca cadastrada</h2>
                <p>
                  {loading
                    ? "Carregando máquinas..."
                    : `${machines.length} máquina(s) disponível(is) na biblioteca`}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="machine-support-empty">Carregando máquinas...</div>
            ) : machines.length === 0 ? (
              <div className="machine-support-empty">
                Nenhuma máquina foi cadastrada ainda. Use o formulário acima para criar a
                primeira.
              </div>
            ) : (
              <div className="machine-support-table">
                <div className="machine-support-table-head">
                  <span>Ordem</span>
                  <span>Máquina</span>
                  <span>Descrição</span>
                  <span>Links</span>
                  <span>Portal</span>
                  <span>Ações</span>
                </div>

                <div className="machine-support-table-body">
                  {machines.map((machine, index) => {
                    const isDragging = draggedMachineId === machine.id;
                    const isDeleting = deletingMachineId === machine.id;

                    return (
                      <div
                        key={machine.id}
                        className={`machine-support-row${isDragging ? " is-dragging" : ""}`}
                        draggable
                        onDragStart={() => handleDragStart(machine.id)}
                        onDragEnd={() => setDraggedMachineId("")}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleDrop(machine.id)}
                      >
                        <div className="machine-support-order">
                          <span className="machine-support-drag">::</span>
                          <strong>{index + 1}</strong>
                        </div>

                        <div className="machine-support-machine">
                          <div className="machine-support-thumb">
                            {machine.imageUrl ? (
                              <img src={machine.imageUrl} alt={machine.title} />
                            ) : (
                              <span>Sem foto</span>
                            )}
                          </div>

                          <div className="machine-support-machine-copy">
                            <strong>{machine.title}</strong>
                            <small>
                              {machine.imageOriginalName
                                ? `${machine.imageOriginalName} • ${formatFileSize(
                                    machine.imageSize
                                  )}`
                                : "Imagem não cadastrada"}
                            </small>
                            <small>Cadastrado em {formatDateTime(machine.createdAt)}</small>
                          </div>
                        </div>

                        <div className="machine-support-description">
                          <strong>Resumo</strong>
                          <small>{machine.description || "Sem descrição."}</small>
                        </div>

                        <div className="machine-support-links-col">
                          <strong>{machine.links?.length || 0} link(s)</strong>
                          <div className="machine-support-link-chips">
                            {(machine.links || []).map((link) => (
                              <a key={link.id} href={link.url} target="_blank" rel="noreferrer">
                                {link.label}
                              </a>
                            ))}
                          </div>
                        </div>

                        <div className="machine-support-visibility">
                          <strong>
                            {machine.licensedEnabled && machine.realgemsEnabled
                              ? "Licenciado + Realgems"
                              : machine.licensedEnabled
                                ? "Licenciado"
                                : "Realgems"}
                          </strong>
                          <small>
                            {machine.licensedEnabled && machine.realgemsEnabled
                              ? "Visível nos dois acessos."
                              : machine.licensedEnabled
                                ? "Exibida apenas para licenciados."
                                : "Exibida apenas no portal Realgems."}
                          </small>
                        </div>

                        <div className="machine-support-row-actions">
                          <button
                            type="button"
                            className="is-secondary"
                            onClick={() => handleStartEdit(machine)}
                            disabled={Boolean(deletingMachineId)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => handleDeleteMachine(machine.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? "Excluindo..." : "Excluir"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default AdminMachineSupport;
